import { PhysioLayout } from "@/components/layout/physio-layout";
import { useState, useEffect } from "react";
import { Monitor, Plus, Trash2, Loader2, X, CheckCircle2, ExternalLink } from "lucide-react";
import { Button } from "@/components/ui/button";

const RECOMMENDED_MODULES = [
  {
    category: "Inflammatory Arthritis",
    modules: [
      { title: "Rheumatoid Arthritis: Physiotherapy Management", level: "Intermediate", hours: 2 },
      { title: "Axial Spondyloarthritis: Exercise and Physical Activity", level: "Intermediate", hours: 2.5 },
      { title: "Psoriatic Arthritis: MSK Assessment and Management", level: "Intermediate", hours: 2 },
    ],
  },
  {
    category: "Connective Tissue Diseases",
    modules: [
      { title: "Systemic Lupus Erythematosus: Physio Perspectives", level: "Advanced", hours: 2 },
      { title: "Systemic Sclerosis: Rehabilitation Approaches", level: "Advanced", hours: 1.5 },
      { title: "Myositis and Inflammatory Muscle Disease", level: "Advanced", hours: 2 },
    ],
  },
  {
    category: "Osteoarthritis & Bone Health",
    modules: [
      { title: "NICE OA Guidelines: Exercise First Approach", level: "Foundation", hours: 1.5 },
      { title: "Osteoporosis: Assessment and Falls Prevention", level: "Intermediate", hours: 2 },
    ],
  },
  {
    category: "Professional Practice",
    modules: [
      { title: "MSK Physiotherapy: Shared Decision Making", level: "Foundation", hours: 1 },
      { title: "Research Evidence in Practice", level: "Foundation", hours: 2 },
      { title: "Clinical Supervision Skills for Physiotherapists", level: "Intermediate", hours: 3 },
    ],
  },
  {
    category: "Advanced Practice",
    modules: [
      { title: "Extended Scope Practice: Frameworks and Governance", level: "Advanced", hours: 3 },
      { title: "Injection Therapy in MSK: Principles", level: "Advanced", hours: 2.5 },
    ],
  },
];

const LEVEL_COLOURS: Record<string, string> = {
  Foundation: "bg-blue-50 text-blue-700 border-blue-200",
  Intermediate: "bg-amber-50 text-amber-700 border-amber-200",
  Advanced: "bg-violet-50 text-violet-700 border-violet-200",
};

interface CpdActivity {
  id: number;
  title: string;
  date: string;
  hours: number;
  activityUndertaken: string;
}

export default function IcspLearningLog() {
  const [logged, setLogged] = useState<CpdActivity[]>([]);
  const [showForm, setShowForm] = useState(false);
  const [title, setTitle] = useState("");
  const [date, setDate] = useState(new Date().toISOString().split("T")[0]);
  const [hours, setHours] = useState("");
  const [activity, setActivity] = useState("");
  const [saving, setSaving] = useState(false);
  const [deletingId, setDeletingId] = useState<number | null>(null);

  const load = async () => {
    const res = await fetch(`/api/physio/cpd?year=${new Date().getFullYear()}`);
    if (res.ok) {
      const data: CpdActivity[] = await res.json();
      setLogged(data.filter(a => a.activityUndertaken?.toLowerCase().includes("icsp") || a.activityUndertaken?.toLowerCase().includes("icsip") || a.title?.toLowerCase().includes("icsp")));
    }
  };

  useEffect(() => { load(); }, []);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSaving(true);
    try {
      const res = await fetch("/api/physio/cpd", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          title: title.trim(),
          date,
          category: "educational",
          hours: parseFloat(hours),
          learningNeed: "iCSP online learning — continuing professional development",
          activityUndertaken: `iCSP: ${activity.trim()}`,
          outcome: "Completed iCSP online module",
          patientImpact: "Enhanced specialist knowledge applied to patient care",
        }),
      });
      if (res.ok) { setShowForm(false); load(); setTitle(""); setHours(""); setActivity(""); }
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id: number) => {
    if (!window.confirm("Delete this activity?")) return;
    setDeletingId(id);
    try {
      await fetch(`/api/physio/cpd/${id}`, { method: "DELETE" });
      setLogged(prev => prev.filter(a => a.id !== id));
    } finally {
      setDeletingId(null);
    }
  };

  const totalHours = logged.reduce((s, a) => s + a.hours, 0);

  return (
    <PhysioLayout title="iCSP Learning Log">
      <div className="space-y-6">
        <div className="bg-gradient-to-br from-emerald-800 to-emerald-950 rounded-2xl p-6 text-white">
          <div className="flex items-center gap-3 mb-3">
            <Monitor className="h-8 w-8 text-emerald-300" />
            <div>
              <h2 className="text-xl font-bold">iCSP Online Learning Log</h2>
              <p className="text-emerald-300 text-sm">CSP Interactive Learning · Webinars · eLearning</p>
            </div>
          </div>
          <p className="text-emerald-200 text-sm leading-relaxed mb-4">
            Log your iCSP platform learning, webinars, and online courses. Completed modules are stored as CPD activities in the Educational category and count towards your HCPC portfolio.
          </p>
          <div className="flex gap-4">
            <div className="bg-white/10 rounded-xl p-3">
              <p className="text-xl font-bold">{logged.length}</p>
              <p className="text-xs text-emerald-300">Modules logged</p>
            </div>
            <div className="bg-white/10 rounded-xl p-3">
              <p className="text-xl font-bold">{totalHours.toFixed(1)}</p>
              <p className="text-xs text-emerald-300">Hours this year</p>
            </div>
            <div className="flex items-center">
              <a
                href="https://www.csp.org.uk/professional-clinical/cpd-elearning"
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center gap-2 text-sm text-emerald-300 hover:text-white font-medium transition-colors"
              >
                <ExternalLink className="h-4 w-4" /> Open iCSP platform
              </a>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-2xl border border-slate-200 p-6">
          <div className="flex items-center justify-between mb-4">
            <h3 className="font-bold text-slate-900">Log Completed Module</h3>
            {!showForm && (
              <Button onClick={() => setShowForm(true)} className="bg-emerald-700 hover:bg-emerald-800 text-white gap-2 text-sm">
                <Plus className="h-4 w-4" /> Log Module
              </Button>
            )}
          </div>

          {showForm && (
            <form onSubmit={handleSubmit} className="bg-emerald-50 border border-emerald-200 rounded-xl p-4 mb-4 space-y-3">
              <div className="flex items-center justify-between mb-1">
                <span className="text-sm font-semibold text-emerald-800">iCSP Module / Webinar</span>
                <button type="button" onClick={() => setShowForm(false)}><X className="h-4 w-4 text-slate-400" /></button>
              </div>
              <input value={title} onChange={e => setTitle(e.target.value)} placeholder="Module title *" required className="w-full h-9 px-3 text-sm border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-emerald-500" />
              <div className="grid grid-cols-2 gap-3">
                <input type="date" value={date} onChange={e => setDate(e.target.value)} required className="h-9 px-3 text-sm border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-emerald-500" />
                <input type="number" min="0.5" step="0.5" value={hours} onChange={e => setHours(e.target.value)} placeholder="Hours *" required className="h-9 px-3 text-sm border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-emerald-500" />
              </div>
              <input value={activity} onChange={e => setActivity(e.target.value)} placeholder="Module description / key learning *" required className="w-full h-9 px-3 text-sm border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-emerald-500" />
              <div className="flex gap-2">
                <Button type="submit" disabled={saving} className="bg-emerald-700 hover:bg-emerald-800 text-white gap-2 text-sm">
                  {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <CheckCircle2 className="h-4 w-4" />}
                  {saving ? "Saving..." : "Log Module"}
                </Button>
                <Button type="button" variant="outline" onClick={() => setShowForm(false)} className="text-sm">Cancel</Button>
              </div>
            </form>
          )}

          {logged.length === 0 ? (
            <p className="text-sm text-slate-500 text-center py-4">No iCSP modules logged yet. Use the button above to record completed online learning.</p>
          ) : (
            <div className="space-y-2">
              {logged.map((a) => (
                <div key={a.id} className="flex items-center gap-4 p-3 bg-slate-50 rounded-xl">
                  <div className="flex-1">
                    <p className="text-sm font-medium text-slate-800">{a.title}</p>
                    <p className="text-xs text-slate-500">{new Date(a.date).toLocaleDateString("en-GB")} · {a.hours} hrs</p>
                  </div>
                  <button onClick={() => handleDelete(a.id)} disabled={deletingId === a.id} className="text-slate-400 hover:text-red-500 transition-colors">
                    {deletingId === a.id ? <Loader2 className="h-4 w-4 animate-spin" /> : <Trash2 className="h-4 w-4" />}
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>

        <div className="bg-white rounded-2xl border border-slate-200 p-6">
          <h3 className="font-bold text-slate-900 mb-4">Recommended iCSP Modules — Rheumatology Physiotherapy</h3>
          <p className="text-sm text-slate-500 mb-4">These modules are relevant to rheumatology physiotherapy practice. Access them via the iCSP platform (CSP member login required).</p>
          <div className="space-y-5">
            {RECOMMENDED_MODULES.map((cat) => (
              <div key={cat.category}>
                <h4 className="text-xs font-semibold uppercase tracking-wider text-slate-400 mb-2">{cat.category}</h4>
                <div className="space-y-2">
                  {cat.modules.map((mod) => (
                    <div key={mod.title} className="flex items-center justify-between p-3 bg-slate-50 rounded-xl">
                      <div>
                        <p className="text-sm font-medium text-slate-800">{mod.title}</p>
                        <div className="flex items-center gap-2 mt-1">
                          <span className={`text-xs px-2 py-0.5 rounded-full border ${LEVEL_COLOURS[mod.level]}`}>{mod.level}</span>
                          <span className="text-xs text-slate-400">{mod.hours} hrs</span>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
          <p className="text-xs text-slate-400 mt-4">Module availability subject to CSP iCSP platform updates. Visit csp.org.uk/cpd-elearning for the full catalogue.</p>
        </div>
      </div>
    </PhysioLayout>
  );
}
